// ============================================================
// Datei: KasseDesktop.jsx
// ============================================================

import React, { useMemo, useState } from "react";
import "./kasse.css";

// Volunteers
import VolunteerList from "../../../components/VolunteerList";
import ShiftTable from "../../../components/shiftTable/ShiftTable";
import EditVolunteerModal from "../../../components/EditVolunteerModal";

// Guests
import GuestRegistration from "../../../components/GuestRegistration";
import GuestList from "../../../components/guestList/GuestList";
import EditGuestModal from "../../../components/guestList/EditGuestModal";
import DeleteGuestModal from "../../../components/guestList/DeleteGuestModal";

// Formular
import ManualVolunteerRegisterForm from "../../../components/manualVolunteerRegisterForm/ManualVolunteerRegisterForm";
import ShiftPrintList from "../../../components/ShiftPrintList";
import VolunteerPool from "../../../components/VolunteerPool";

const ROLE_ID_ADMIN = 1;
const ROLE_ID_OK = 2;
const ROLE_ID_SUPERHELFER = 3;
const ROLE_ID_HELFER = 4;

const getVolunteerRoleId = (volunteer) => {
	if (!volunteer) return null;

	if (volunteer.roleId != null) return Number(volunteer.roleId);
	if (volunteer.role_id != null) return Number(volunteer.role_id);

	if (volunteer.role?.roleId != null) return Number(volunteer.role.roleId);
	if (volunteer.role?.role_id != null) return Number(volunteer.role.role_id);
	if (volunteer.role?.id != null) return Number(volunteer.role.id);

	return null;
};

const isHiddenAdminUser = (volunteer) => {
	const firstName = String(
		volunteer?.firstName || volunteer?.vorname || ""
	).trim().toLowerCase();

	const lastName = String(
		volunteer?.lastName || volunteer?.name || ""
	).trim().toLowerCase();

	return firstName === "admin" && lastName === "admin";
};

const getDepartmentName = (shift) => {
	if (!shift) return "Unbekannt";

	if (typeof shift.departmentName === "string" && shift.departmentName.trim()) {
		return shift.departmentName.trim();
	}

	if (typeof shift.department === "string" && shift.department.trim()) {
		return shift.department.trim();
	}

	if (
		shift.departmentDto &&
		typeof shift.departmentDto.department === "string" &&
		shift.departmentDto.department.trim()
	) {
		return shift.departmentDto.department.trim();
	}

	if (
		shift.departmentDto &&
		typeof shift.departmentDto.name === "string" &&
		shift.departmentDto.name.trim()
	) {
		return shift.departmentDto.name.trim();
	}

	if (
		shift.department &&
		typeof shift.department.department === "string" &&
		shift.department.department.trim()
	) {
		return shift.department.department.trim();
	}

	return "Unbekannt";
};

const getVolunteerFullName = (volunteer) => {
	const firstName = String(
		volunteer?.firstName ||
		volunteer?.vorname ||
		volunteer?.firstname ||
		""
	).trim();

	const lastName = String(
		volunteer?.lastName ||
		volunteer?.name ||
		volunteer?.lastname ||
		""
	).trim();

	return `${firstName} ${lastName}`.trim();
};

const getVolunteerDeployments = (volunteer) => {
	if (Array.isArray(volunteer?.volunteerDeployments)) return volunteer.volunteerDeployments;
	if (Array.isArray(volunteer?.volunteer_deployments)) return volunteer.volunteer_deployments;
	if (Array.isArray(volunteer?.volunteerDeployment)) return volunteer.volunteerDeployment;
	if (Array.isArray(volunteer?.deployments)) return volunteer.deployments;
	return [];
};

const getShiftId = (shift) => {
	if (shift?.shiftId != null) return Number(shift.shiftId);
	if (shift?.shift_id != null) return Number(shift.shift_id);
	if (shift?.id != null) return Number(shift.id);
	return null;
};

const getDeploymentShiftId = (deployment) => {
	if (deployment?.shiftId != null) return Number(deployment.shiftId);
	if (deployment?.shift_id != null) return Number(deployment.shift_id);
	if (deployment?.shift?.shiftId != null) return Number(deployment.shift.shiftId);
	if (deployment?.shift?.shift_id != null) return Number(deployment.shift.shift_id);
	if (deployment?.shift?.id != null) return Number(deployment.shift.id);
	return null;
};

const normalizeNames = (entries) => {
	if (!Array.isArray(entries)) return [];

	return entries
		.map((entry) => {
			if (typeof entry === "string") return entry.trim();

			const firstName = String(
				entry?.firstName || entry?.vorname || entry?.firstname || ""
			).trim();

			const lastName = String(
				entry?.lastName || entry?.name || entry?.lastname || ""
			).trim();

			return `${firstName} ${lastName}`.trim();
		})
		.filter(Boolean);
};

const enrichShiftsWithVolunteerNames = (shifts, volunteers) => {
	const shiftIdToNames = new Map();

	(volunteers || []).forEach((volunteer) => {
		if (isHiddenAdminUser(volunteer)) return;

		const roleId = getVolunteerRoleId(volunteer);
		if (![ROLE_ID_OK, ROLE_ID_HELFER, ROLE_ID_SUPERHELFER].includes(roleId)) return;

		const fullName = getVolunteerFullName(volunteer);
		if (!fullName) return;

		const deployments = getVolunteerDeployments(volunteer);

		deployments.forEach((deployment) => {
			const shiftId = getDeploymentShiftId(deployment);
			if (shiftId == null) return;

			if (!shiftIdToNames.has(shiftId)) {
				shiftIdToNames.set(shiftId, []);
			}

			const currentNames = shiftIdToNames.get(shiftId);
			if (!currentNames.includes(fullName)) {
				currentNames.push(fullName);
			}
		});
	});

	return (shifts || []).map((shift) => {
		const shiftId = getShiftId(shift);
		const namesFromVolunteerMap = shiftIdToNames.get(shiftId) || [];

		const existingNames = [
			...normalizeNames(shift?.volunteerNames),
			...normalizeNames(shift?.libellen),
			...normalizeNames(shift?.assignedVolunteerNames),
			...normalizeNames(shift?.assignedVolunteers),
			...normalizeNames(shift?.volunteers)
		];

		const mergedNames = [...new Set([...existingNames, ...namesFromVolunteerMap])];

		return {
			...shift,
			volunteerNames: mergedNames
		};
	});
};


// ============================================================
// GÄSTE-FILTER FÜR NORMALE OK-ANSICHT
//
// Zweck:
// - Normale Kasse-Gästeliste zeigt nur Gäste des eingeloggten OK.
// - Adminansicht-Gästeliste zeigt alle geladenen Gäste.
// ============================================================
const getLoggedInVolunteerId = () => {
	return localStorage.getItem("volunteerId") || localStorage.getItem("volunteer_id") || "";
};

const getLoggedInUsername = () => {
	return String(localStorage.getItem("username") || "").trim().toLowerCase();
};

const isGuestRecordedByCurrentOk = (guest) => {
	const loggedInVolunteerId = getLoggedInVolunteerId();
	const loggedInUsername = getLoggedInUsername();

	if (loggedInVolunteerId && guest?.recordedById != null) {
		return String(guest.recordedById) === String(loggedInVolunteerId);
	}

	if (loggedInUsername && guest?.recordedByFullName) {
		return String(guest.recordedByFullName).toLowerCase().includes(loggedInUsername);
	}

	return false;
};

const filterGuestsForCurrentOk = (guests = []) => {
	if (!Array.isArray(guests)) {
		return [];
	}

	return guests.filter(isGuestRecordedByCurrentOk);
};

const KasseDesktop = ({
	volunteers,
	setVolunteers,
	shifts,
	guests,
	selectedVolunteer,
	setSelectedVolunteer,
	activeTab,
	setActiveTab,
	reloadGuests,
	downloadCcEmails,
	reloadVolunteers,
	allVolunteers = [],
	allShifts = []
}) => {
	const [guestToEdit, setGuestToEdit] = useState(null);
	const [guestToDelete, setGuestToDelete] = useState(null);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [adminSubTab, setAdminSubTab] = useState("helpers");

	const kasseEingang = shifts.filter((s) => s.shiftId >= 201 && s.shiftId <= 207);
	const kasseHelfer = shifts.filter((s) => s.shiftId >= 211 && s.shiftId <= 216);

	const tabs = [
		{ id: "overview", label: "Übersicht" },
		{ id: "helpers", label: "Libellen" },
		{ id: "shifts", label: "Schichtplan" },
		{ id: "guests", label: "Gästeliste" },
		{ id: "extras", label: "Extrawürscht" },
		{ id: "adminview", label: "Adminansicht" }
	];

	const visibleAllVolunteers = useMemo(() => {
		return (allVolunteers || []).filter((v) => {
			const roleId = getVolunteerRoleId(v);

			if (isHiddenAdminUser(v)) return false;
			if (roleId === ROLE_ID_ADMIN) return false;

			return true;
		});
	}, [allVolunteers]);

	const helperVolunteers = useMemo(() => {
		return visibleAllVolunteers.filter((v) => {
			const roleId = getVolunteerRoleId(v);
			return roleId === ROLE_ID_HELFER || roleId === ROLE_ID_SUPERHELFER;
		});
	}, [visibleAllVolunteers]);

	const okVolunteers = useMemo(() => {
		return visibleAllVolunteers.filter((v) => {
			const roleId = getVolunteerRoleId(v);
			return roleId === ROLE_ID_OK;
		});
	}, [visibleAllVolunteers]);

	const enrichedAllShifts = useMemo(() => {
		return enrichShiftsWithVolunteerNames(allShifts, allVolunteers);
	}, [allShifts, allVolunteers]);

	const allShiftsByDepartment = useMemo(() => {
		const groups = {};

		(enrichedAllShifts || []).forEach((shift) => {
			const departmentName = getDepartmentName(shift);

			if (!groups[departmentName]) {
				groups[departmentName] = [];
			}

			groups[departmentName].push(shift);
		});

		return Object.entries(groups)
			.map(([departmentName, departmentShifts]) => [
				departmentName,
				departmentShifts
			])
			.sort(([a], [b]) => a.localeCompare(b, "de", { sensitivity: "base" }));
	}, [enrichedAllShifts]);

	// ============================================================
	// Normale Gästeliste: nur eigene Gäste des eingeloggten OK
	// Adminansicht: verwendet weiterhin guests komplett.
	// ============================================================
	const ownGuests = useMemo(() => {
		return filterGuestsForCurrentOk(guests);
	}, [guests]);

	const handleAdminToggleVisibility = () => {
		// gleich wie beim Admin aktuell ohne Umschaltlogik
	};

	return (
		<div className="kasse-desktop-shell">
			<div className="tabs-container kasse-tabs-container">
				<div className="tab-bar kasse-tab-bar">
					{tabs.map((t) => (
						<button
							key={t.id}
							className={
								"tab-button" +
								(activeTab === t.id ? " tab-button-active" : "")
							}
							onClick={() => setActiveTab(t.id)}
							type="button"
						>
							{t.label}
						</button>
					))}
				</div>

				<div className="tab-panels kasse-tab-panels">
					{activeTab === "overview"}

					{activeTab === "helpers" && (
						<div className="helpers-layout">
							<div className="helpers-column helpers-column-left">
								<VolunteerList
									volunteers={volunteers}
									setVolunteers={setVolunteers}
									onEdit={(v) => setSelectedVolunteer(v)}
									reloadVolunteers={reloadVolunteers}
								/>
							</div>

							<div className="helpers-column helpers-column-right">
								<VolunteerPool
									departmentName="Kasse"
									onAssigned={() => {
										if (reloadVolunteers) reloadVolunteers();
									}}
								/>
							</div>
						</div>
					)}

					{activeTab === "shifts" && (
						<div className="kasse-shifts-panel">
							<section className="kasse-shift-section">
								<h2 className="kasse-subheading">Eingangskasse</h2>
								<ShiftTable
									shifts={kasseEingang}
									departmentName="Kasse"
									autoRefresh={true}
									filterFn={(shift) => shift.shiftId >= 201 && shift.shiftId <= 207}
								/>
							</section>

							<section className="kasse-shift-section">
								<h2 className="kasse-subheading">VIP-Kasse</h2>
								<ShiftTable
									shifts={kasseHelfer}
									departmentName="Kasse"
									autoRefresh={true}
									filterFn={(shift) => shift.shiftId >= 211 && shift.shiftId <= 216}
								/>
							</section>
						</div>
					)}

					{activeTab === "guests" && (
						<div className="kasse-guests-panel">
							<GuestRegistration refreshGuestList={reloadGuests} />

							<GuestList
								guests={ownGuests}
								onEdit={(g) => setGuestToEdit(g)}
								onDelete={(g) => setGuestToDelete(g)}
							/>
						</div>
					)}

					{activeTab === "extras" && (
						<div className="kasse-extras-panel">
							<button
								className="cc-mail-card"
								onClick={downloadCcEmails}
								type="button"
							>
								<span className="cc-mail-icon">✉️</span>
								<span className="cc-mail-text">
									<span className="cc-mail-title">
										CC-Liste Helferfest herunterladen
									</span>
									<span className="cc-mail-subtitle">
										Für Outlook &amp; Gmail geeignet
									</span>
								</span>
							</button>
							<ShiftPrintList />
						</div>
					)}

					{activeTab === "adminview" && (
						<div className="kasse-adminview-panel">
							<div className="tabs-container kasse-tabs-container admin-subtabs-container">
								<div className="tab-bar kasse-tab-bar admin-subtab-bar">
									<button
										className={
											"tab-button" +
											(adminSubTab === "helpers" ? " tab-button-active" : "")
										}
										onClick={() => setAdminSubTab("helpers")}
										type="button"
									>
										Helferliste
									</button>

									<button
										className={
											"tab-button" +
											(adminSubTab === "shifts" ? " tab-button-active" : "")
										}
										onClick={() => setAdminSubTab("shifts")}
										type="button"
									>
										Schichtplan
									</button>

									<button
										className={
											"tab-button" +
											(adminSubTab === "guests" ? " tab-button-active" : "")
										}
										onClick={() => setAdminSubTab("guests")}
										type="button"
									>
										Gästeliste
									</button>
								</div>

								<div className="tab-panels kasse-tab-panels">
									{adminSubTab === "helpers" && (
										<section className="helpers-layout">
											<div className="helpers-column helpers-column-left">
												<h3 className="kasse-subheading">Helfer und Superhelfer</h3>
												<VolunteerList
													volunteers={helperVolunteers}
													setVolunteers={setVolunteers}
													reloadVolunteers={reloadVolunteers}
												/>
											</div>

											<div className="helpers-column helpers-column-right">
												<h3 className="kasse-subheading">OK-Mitglieder</h3>
												<VolunteerList
													volunteers={okVolunteers}
													setVolunteers={setVolunteers}
													reloadVolunteers={reloadVolunteers}
												/>
											</div>
										</section>
									)}

									{adminSubTab === "shifts" && (
										<div className="admin-shifts-panel">
											<h3 className="kasse-subheading">Alle Schichten aller Ressorts</h3>

											{allShiftsByDepartment.length > 0 ? (
												allShiftsByDepartment.map(([departmentName, departmentShifts]) => (
													<section key={departmentName} className="admin-shift-section">
														<h3 className="kasse-subheading">{departmentName}</h3>
														<ShiftTable
															shifts={departmentShifts}
															departmentName={departmentName}
															autoRefresh={false}
															onToggleVisibility={handleAdminToggleVisibility}
														/>
													</section>
												))
											) : (
												<ShiftTable
													shifts={[]}
													autoRefresh={false}
													onToggleVisibility={handleAdminToggleVisibility}
												/>
											)}
										</div>
									)}

									{adminSubTab === "guests" && (
										<div className="admin-guests-panel">
											<GuestRegistration
												refreshGuestList={reloadGuests}
												guests={guests}
												guestAccessMode="admin"
												forceAdminGuestRules={true}
											/>

											<GuestList
												guests={guests}
												onEdit={(g) => setGuestToEdit(g)}
												onDelete={(g) => setGuestToDelete(g)}
											/>
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{showCreateModal && (
				<div
					className="modal-overlay"
					onClick={() => setShowCreateModal(false)}
				>
					<div
						className="modal-content"
						onClick={(e) => e.stopPropagation()}
					>
						<ManualVolunteerRegisterForm
							department="kasse"
							onCreated={() => {
								setShowCreateModal(false);
								reloadVolunteers && reloadVolunteers();
							}}
						/>

						<button
							className="modal-close-button"
							onClick={() => setShowCreateModal(false)}
							type="button"
						>
							Schliessen
						</button>
					</div>
				</div>
			)}

			{selectedVolunteer && (
				<EditVolunteerModal
					volunteer={selectedVolunteer}
					onClose={() => setSelectedVolunteer(null)}
					onSaved={reloadVolunteers}
				/>
			)}

			{guestToEdit && (
				<EditGuestModal
					guest={guestToEdit}
					onClose={() => setGuestToEdit(null)}
					onSaved={() => {
						setGuestToEdit(null);
						reloadGuests();
					}}
				/>
			)}

			{guestToDelete && (
				<DeleteGuestModal
					guest={guestToDelete}
					onClose={() => setGuestToDelete(null)}
					onDeleted={() => {
						setGuestToDelete(null);
						reloadGuests();
					}}
				/>
			)}
		</div>
	);
};

export default KasseDesktop;